import React from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { STATUS_STYLES } from "@/lib/layers";
import { SOURCE } from "@/lib/constants";

// Layer / chain status badge. Never relies on colour alone — always shows text + icon.
export function StatusBadge({ status, className = "", testId }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.READY;
  const Icon = Icons[s.icon] || Icons.Circle;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider ${s.bg} ${s.text} ${s.border} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {status}
    </span>
  );
}

// Provenance source badge — LOCAL vs SIMULATED vs EXTERNAL.
export function ProvenanceBadge({ source = "local", className = "", testId }) {
  const map = {
    local: { text: "text-cyan-300", bg: "bg-cyan-950", border: "border-cyan-600", icon: "ShieldCheck" },
    simulated: { text: "text-amber-300", bg: "bg-amber-950", border: "border-amber-600", icon: "FlaskConical" },
    external: { text: "text-slate-400", bg: "bg-slate-800", border: "border-slate-600", icon: "Globe" },
  };
  const s = map[source] || map.local;
  const Icon = Icons[s.icon];
  return (
    <span
      data-testid={testId}
      title={SOURCE[source]?.detail}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.text} ${s.border} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {SOURCE[source]?.label || source}
    </span>
  );
}

// Signature status badge — VERIFIED / INVALID / UNSIGNED (text + icon, not colour alone).
export function SignatureBadge({ state = "UNSIGNED RECEIPT", className = "", testId }) {
  const map = {
    "SIGNATURE VERIFIED": { text: "text-emerald-300", bg: "bg-emerald-950", border: "border-emerald-600", icon: "BadgeCheck" },
    "SIGNATURE INVALID": { text: "text-rose-300", bg: "bg-rose-950", border: "border-rose-500", icon: "ShieldX" },
    "UNSIGNED RECEIPT": { text: "text-slate-400", bg: "bg-slate-800", border: "border-slate-600", icon: "PenOff" },
  };
  const s = map[state] || map["UNSIGNED RECEIPT"];
  const Icon = Icons[s.icon] || Icons.PenOff;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.text} ${s.border} ${className}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {state}
    </span>
  );
}

export function PageHeader({ overline, title, subtitle, icon, actions }) {  const Icon = icon ? Icons[icon] : null;
  return (
    <div className="mb-6 flex flex-col gap-4 border-b border-slate-800 pb-5 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {overline && (
          <p className="mb-1 font-mono text-xs font-bold uppercase tracking-[0.25em] text-cyan-500">{overline}</p>
        )}
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-3xl">
          {Icon && <Icon className="h-6 w-6 text-cyan-400" aria-hidden="true" />}
          {title}
        </h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionCard({ title, icon, children, className = "", right, testId }) {
  const Icon = icon ? Icons[icon] : null;
  return (
    <div
      data-testid={testId}
      className={`min-w-0 rounded-lg border border-slate-800 bg-[#0D121D] shadow-lg transition-colors hover:border-slate-700 ${className}`}
    >
      {title && (
        <div className="flex items-center justify-between gap-2 border-b border-slate-800 px-4 py-3">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-cyan-400">
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {title}
          </h3>
          {right}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Mono({ children, className = "", testId }) {
  return (
    <code
      data-testid={testId}
      className={`break-all rounded border border-cyan-950 bg-slate-950/80 px-1.5 py-0.5 font-mono text-xs text-cyan-300 ${className}`}
    >
      {children}
    </code>
  );
}

export function InfoNote({ children, tone = "info", className = "", ...rest }) {
  const tones = {
    info: "border-slate-700 bg-slate-900/60 text-slate-300",
    warn: "border-amber-700 bg-amber-950/40 text-amber-200",
    danger: "border-rose-700 bg-rose-950/40 text-rose-200",
    ok: "border-emerald-700 bg-emerald-950/40 text-emerald-200",
  };
  const iconMap = { info: "Info", warn: "AlertTriangle", danger: "ShieldAlert", ok: "CheckCircle2" };
  const Icon = Icons[iconMap[tone]];
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs leading-relaxed ${tones[tone]} ${className}`} {...rest}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

// Copy text to clipboard and surface a toast. Never throws / never leaves an
// unhandled rejection when the Clipboard API is unavailable.
export async function copyText(text, message = "Copied") {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text));
      toast.success(message);
      return true;
    }
  } catch {
    /* fall through to error toast */
  }
  toast.error("Copy failed", { description: "Clipboard is unavailable in this context." });
  return false;
}
